import { createFileRoute, Link } from "@tanstack/react-router";
import {
  LearnArticleLayout,
  learnArticleHead,
} from "@/components/LearnArticleLayout";

const PATH = "/learn/dental-marketing-cost-colorado";
const TITLE = "How Much Does Dental Marketing Cost in Colorado? (2026)";
const DESCRIPTION =
  "Real 2026 pricing for dental SEO, Google Ads, Maps, and websites in Colorado — what new-patient acquisition actually costs in Denver and Colorado Springs.";

export const Route = createFileRoute("/learn/dental-marketing-cost-colorado")({
  head: () => learnArticleHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: Article,
});

function Article() {
  return (
    <LearnArticleLayout
      category="Pricing"
      title={TITLE}
      intro="Colorado dental practices are competing for some of the highest-value patients in the country — and paying CPCs that reflect it. Here's what a real new-patient acquisition program costs in 2026."
      readTime="5 min read"
      publishedDate="2026-06-22"
      path={PATH}
      definition={{
        term: "What does dental marketing cost in Colorado?",
        body: "Most Colorado dental practices invest $2,500–$5,500/month for SEO, Google Maps, and AI search, plus $3,000–$12,000/month in Google Ads spend. A custom dental website starts at $1,850 one-time. Cost per new patient typically lands $150–$450 in metro markets.",
      }}
      toc={[
        { id: "what-makes-dental-different", label: "What makes dental different" },
        { id: "real-numbers", label: "Real Colorado dental numbers" },
        { id: "by-city", label: "Cost by city" },
        { id: "cost-per-patient", label: "Cost per new patient" },
        { id: "specialty-vs-general", label: "Specialty vs. general dentistry" },
      ]}
      faqs={[
        {
          q: "What's a healthy marketing budget for a Colorado dental practice?",
          a: "6–9% of collections for established practices, 10–15% for practices in growth mode. A $1.2M Colorado practice should plan $6k–$10k/month all-in. Our $1,650/month Growth Marketing program is the SEO/Maps/AI side; PPC ad spend is on top.",
        },
        {
          q: "Is SEO or Google Ads better for new dental patients in Colorado?",
          a: "Both. Google Ads + LSA fill the chair fast — within 2–4 weeks. SEO and Maps build the cheaper, compounding patient pipeline that pays for itself over time. Most healthy practices run both, with SEO eventually carrying 60–70% of new patients.",
        },
        {
          q: "Do dentists in Colorado need a custom website?",
          a: "Yes. Patients evaluate trust signals in seconds — modern photography, real provider bios, online booking, and HIPAA-compliant forms all matter. A template site converts 30–50% worse than a custom one in this category.",
        },
        {
          q: "How long until a Colorado dental SEO campaign pays back?",
          a: "Most practices break even on SEO between months 4 and 7, depending on starting reviews count, GBP completeness, and competitive density. Specialty practices (ortho, implants) pay back faster because patient values are higher.",
        },
      ]}
    >
      <h2 id="what-makes-dental-different">What makes dental different</h2>
      <p>
        Dental marketing is constrained by HIPAA, ADA guidelines, and patient
        privacy — which limits the kind of testimonial and retargeting work
        legal in other verticals. It's also driven by patient lifetime value:
        the average Colorado patient is worth $1,200–$4,000 over 5 years, so the
        math supports paying $200–$400 per new patient.
      </p>

      <h2 id="real-numbers">Real Colorado dental numbers</h2>
      <h3>Solo general dentist</h3>
      <p>
        $1,850 site + $1,650/month{" "}
        <Link to="/pricing" className="text-primary underline">Growth Marketing</Link>{" "}
        + $3,000/month Google Ads spend. First-year total ~$65k. Typical outcome:
        20–45 new patients/month by month 4–6.
      </p>
      <h3>Multi-doctor or multi-location practice</h3>
      <p>
        $7,500–$15,000 custom site + $3,500/month managed SEO/Maps per location
        + $6,000–$12,000/month combined ad spend.
      </p>
      <h3>Specialty (ortho, implant, pediatric)</h3>
      <p>
        Similar fixed costs, but worth investing more in conversion-rate work,
        landing pages per procedure, and video content. Plan $8k–$15k/month
        all-in.
      </p>

      <h2 id="by-city">Cost by city</h2>
      <ul>
        <li><Link to="/denver-seo-cost" className="text-primary underline">Denver SEO cost</Link> — the most saturated dental market in the state.</li>
        <li><Link to="/colorado-springs-seo-cost" className="text-primary underline">Colorado Springs SEO cost</Link></li>
        <li><Link to="/boulder-seo-cost" className="text-primary underline">Boulder SEO cost</Link> — premium patient demographics.</li>
        <li><Link to="/fort-collins-seo-cost" className="text-primary underline">Fort Collins SEO cost</Link></li>
      </ul>
      <p>
        See our{" "}
        <Link to="/dental-marketing-colorado" className="text-primary underline">
          dental marketing hub
        </Link>{" "}
        and city pages like{" "}
        <Link to="/dental-seo-denver" className="text-primary underline">dental SEO Denver</Link>{" "}
        for market-specific guidance.
      </p>

      <h2 id="cost-per-patient">Cost per new patient</h2>
      <ul>
        <li>Google Ads: $180–$400 per booked new patient in metro Denver.</li>
        <li>Google Maps / GBP: $45–$110 per new patient call.</li>
        <li>Organic SEO: $35–$90 effective cost per new patient after month 6.</li>
        <li>Internal referrals (driven by reviews + brand): $0 click cost.</li>
      </ul>

      <h2 id="specialty-vs-general">Specialty vs. general dentistry</h2>
      <p>
        Specialty practices (implants, ortho, perio) should spend more per
        patient because case values are 5–20x higher than hygiene-driven general
        practice. General dentists win by stacking PPC, Maps, and review velocity
        to keep the chair full week-over-week.
      </p>
      <p>
        Full pricing on the{" "}
        <Link to="/pricing" className="text-primary underline">pricing page</Link>.
      </p>
    </LearnArticleLayout>
  );
}
